import { useState } from 'react';
import {
  LinkIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  // Clipboard
  ClipboardIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import { Card, Button, Select, Input, IconButton } from '../ui';
import {
  useClientSurveys,
  useCreateSurvey,
  useUpdateSurvey,
  useDeleteSurvey,
  useCompileSurvey,
  useToggleSurveyActive,
} from '../../hooks/useClients';
import { useDatabases } from '../../hooks/useDatabases';

function formatDate(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function SurveyCard({ survey, clientId, dbLookup, expanded: expandedProp, onToggleExpanded }) {
  const updateSurvey = useUpdateSurvey();
  const deleteSurvey = useDeleteSurvey();
  const compileSurvey = useCompileSurvey();
  const toggleActive = useToggleSurveyActive();
  const isActive = survey.isActive !== false; // treat undefined as true
  const [copied, setCopied] = useState(false);
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;
  const toggleExpanded = () => {
    if (isControlled) onToggleExpanded?.();
    else setInternalExpanded((v) => !v);
  };
  const [compileError, setCompileError] = useState(null);

  const baseUrl = (process.env.REACT_APP_BASE_URL || window.location.origin).replace(/\/$/, '');
  const surveyUrl = `${baseUrl}/survey/${survey.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(surveyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = surveyUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleOpen = () => {
    updateSurvey.mutate({ clientId, surveyId: survey.id, isOpen: !survey.isOpen });
  };

  const handleDelete = () => {
    const msg = survey.responseCount > 0
      ? `Delete this survey and all ${survey.responseCount} responses? This cannot be undone.`
      : 'Delete this survey? This cannot be undone.';
    if (!window.confirm(msg)) return;
    deleteSurvey.mutate({ clientId, surveyId: survey.id });
  };

  const handleCompile = async () => {
    setCompileError(null);
    try {
      await compileSurvey.mutateAsync({ clientId, surveyId: survey.id });
    } catch (err) {
      setCompileError(err.response?.data?.error || 'Compile failed.');
    }
  };

  const canDelete = survey.responseCount === 0 || (!survey.isOpen && survey.isActive === false);

  return (
    <div className="border border-border-light dark:border-border rounded-lg overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition"
        onClick={toggleExpanded}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-text-primary-light dark:text-text-primary truncate">{survey.title}</span>
            <span className={`text-[11px] px-1.5 py-0 rounded font-medium flex-shrink-0 ${survey.isOpen
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
              }`}>
              {survey.isOpen ? 'Open' : 'Closed'}
            </span>
            {survey.databaseId && dbLookup[survey.databaseId] && (
              <span className="text-[11px] px-1.5 py-0 rounded font-medium flex-shrink-0 bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                {dbLookup[survey.databaseId]}
              </span>
            )}
            {survey.side && (
              <span className={`text-[11px] px-1.5 py-0 rounded font-medium flex-shrink-0 ${survey.side === 'pre'
                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                }`}>
                {survey.side === 'pre' ? 'Pre' : 'Post'}
              </span>
            )}
          </div>
          <div className="flex gap-3 mt-0.5 flex-wrap">
            <span className="text-xs text-text-muted-light dark:text-text-muted">Created {formatDate(survey.createdAt)}</span>
            <span className="text-xs text-text-muted-light dark:text-text-muted">{survey.responseCount} response{survey.responseCount !== 1 ? 's' : ''}</span>
            {survey.uncompiledCount > 0 && (
              <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">{survey.uncompiledCount} pending compile</span>
            )}
          </div>
        </div>
        {/* Active toggle — only meaningful once compiled */}
        {survey.lastCompiledAt && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleActive.mutate({ clientId, surveyId: survey.id, active: !isActive });
            }}
            disabled={toggleActive.isPending}
            title={isActive ? 'Included in metrics — click to exclude' : 'Excluded from metrics — click to include'}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${isActive ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
          >
            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${isActive ? 'translate-x-[18px]' : 'translate-x-1'
              }`} />
          </button>
        )}
        {expanded ? (
          <ChevronUpIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-text-muted-light dark:text-text-muted flex-shrink-0" />
        )}
      </div>

      {/* Expanded panel */}
      {expanded && (
        <div className="border-t border-border-light dark:border-border px-4 py-4 space-y-3 bg-black/[0.01] dark:bg-white/[0.01]">

          {/* Survey link */}
          <div className="flex items-center gap-2 bg-background-light dark:bg-background rounded-lg px-3 py-2 border border-border-light dark:border-border">
            <LinkIcon className="w-3.5 h-3.5 text-text-muted-light dark:text-text-muted flex-shrink-0" />
            <span className="text-xs text-text-secondary-light dark:text-text-secondary truncate flex-1 font-mono">{surveyUrl}</span>
            
            <IconButton
                icon={copied ? ClipboardDocumentCheckIcon : ClipboardIcon}
                label={copied ? 'Copied!' : 'Copy'}
                variant="secondary"
                size="sm"
                className="flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  copyLink();
                }}
              />
          </div>

          {/* Actions row */}
          <div className="flex flex-wrap gap-2 items-center">
            <a
              href={surveyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary-light dark:text-text-secondary border border-border-light dark:border-border rounded-lg px-3 py-1.5 hover:opacity-75 transition bg-gray-100 dark:bg-background-gray"
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              View Survey
            </a>

            <Button
              variant="secondary"
              size="sm"
              onClick={toggleOpen}
              disabled={updateSurvey.isPending}
            >
              {survey.isOpen ? 'Close Survey' : 'Open Survey'}
            </Button>

            {survey.uncompiledCount > 0 && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleCompile}
                disabled={compileSurvey.isPending}
              >
                {compileSurvey.isPending ? 'Compiling…' : `Compile ${survey.uncompiledCount} response${survey.uncompiledCount !== 1 ? 's' : ''}`}
              </Button>
            )}

            <IconButton
              icon={TrashIcon}
              label="Delete survey"
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={!canDelete || deleteSurvey.isPending}
              className="hover:text-red-500 disabled:opacity-30"
            />
          </div>

          {compileError && <p className="text-xs text-red-500">{compileError}</p>}

          {survey.lastCompiledAt && (
            <div className="border-t border-border-light dark:border-border pt-3 flex items-center gap-2">
              <CheckCircleIcon className="w-3.5 h-3.5 text-text-muted-light dark:text-text-muted flex-shrink-0" />
              <p className="text-xs text-text-muted-light dark:text-text-muted">
                Last compiled {formatDate(survey.lastCompiledAt)} · {isActive ? 'Contributing to metrics' : 'Excluded from metrics'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PairRow({ surveys, clientId, dbLookup }) {
  const [expanded, setExpanded] = useState(false);
  const toggle = () => setExpanded((v) => !v);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      {surveys.map((s) => (
        <SurveyCard
          key={s.id}
          survey={s}
          clientId={clientId}
          dbLookup={dbLookup}
          expanded={expanded}
          onToggleExpanded={toggle}
        />
      ))}
    </div>
  );
}

const STATUS_OPTIONS = [
  { value: 'all',    label: 'All surveys' },
  { value: 'open',   label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

// Group paired surveys (sharing a pairGroupId) together; standalone surveys pass through.
function groupSurveys(surveys) {
  const result = [];
  const pairMap = new Map();
  for (const s of surveys) {
    if (s.pairGroupId) {
      if (!pairMap.has(s.pairGroupId)) {
        const entry = { kind: 'pair', pairGroupId: s.pairGroupId, surveys: [], firstIdx: result.length };
        pairMap.set(s.pairGroupId, entry);
        result.push(entry);
      }
      pairMap.get(s.pairGroupId).surveys.push(s);
    } else {
      result.push({ kind: 'single', survey: s });
    }
  }
  for (const entry of pairMap.values()) {
    entry.surveys.sort((a, b) => (a.side === 'pre' ? -1 : b.side === 'pre' ? 1 : 0));
  }
  return result;
}

export default function ClientSurveyTab({ clientId }) {
  const { data: surveys = [], isLoading, isError } = useClientSurveys(clientId);
  const { data: databases = [] } = useDatabases();
  const dbLookup = Object.fromEntries(databases.map(db => [db.id || db.slug, db.name]));
  const createSurvey = useCreateSurvey();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDatabaseId, setNewDatabaseId] = useState('');
  const [createError, setCreateError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreateError(null);
    try {
      const payload = { clientId, title: newTitle.trim(), description: newDescription.trim() };
      if (newDatabaseId) payload.databaseId = newDatabaseId;
      await createSurvey.mutateAsync(payload);
      setNewTitle('');
      setNewDescription('');
      setNewDatabaseId('');
      setCreating(false);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create survey.');
    }
  };

  const filteredSurveys = surveys.filter((s) => {
    if (filterStatus === 'open') return s.isOpen;
    if (filterStatus === 'closed') return !s.isOpen;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={STATUS_OPTIONS}
          />
        </div>
        {!creating && (
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            <span className="flex items-center gap-1">
              <PlusIcon className="w-4 h-4" />
              New Survey
            </span>
          </Button>
        )}
      </div>

      {/* Create form */}
      {creating && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">Survey Title *</label>
              <Input
                autoFocus
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. AI Awareness Survey — Q1 2025"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-text-secondary-light dark:text-text-secondary mb-1">Description (optional)</label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Shown to respondents at the top of the survey"
                rows={2}
                className="w-full px-3 py-2 text-sm border border-border-light dark:border-border rounded-lg bg-transparent text-text-primary-light dark:text-text-primary placeholder:text-text-muted-light dark:placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-primary resize-none"
              />
            </div>
            {databases.length > 1 && (
              <Select
                label="Database"
                value={newDatabaseId}
                onChange={(e) => setNewDatabaseId(e.target.value)}
              >
                <option value="">Default (AI Readiness)</option>
                {databases.map((db) => (
                  <option key={db.id} value={db.id}>{db.name}</option>
                ))}
              </Select>
            )}
            {createError && <p className="text-xs text-red-500">{createError}</p>}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" size="sm" disabled={!newTitle.trim() || createSurvey.isPending}>
                {createSurvey.isPending ? 'Creating…' : 'Create Survey'}
              </Button>
              <Button type="button" variant="secondary" size="sm" onClick={() => { setCreating(false); setNewTitle(''); setNewDescription(''); setNewDatabaseId(''); setCreateError(null); }}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Survey list */}
      {isLoading ? (
        <p className="text-sm text-text-muted-light dark:text-text-muted py-4">Loading surveys…</p>
      ) : isError ? (
        <p className="text-sm text-red-500 py-4">Failed to load surveys.</p>
      ) : surveys.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <LinkIcon className="w-8 h-8 text-text-muted-light dark:text-text-muted mx-auto mb-3" />
            <p className="text-sm text-text-secondary-light dark:text-text-secondary">No surveys yet.</p>
            <p className="text-xs text-text-muted-light dark:text-text-muted mt-1">Create a survey to generate a public link for this client.</p>
          </div>
        </Card>
      ) : filteredSurveys.length === 0 ? (
        <p className="text-sm text-text-muted-light dark:text-text-muted py-4">No surveys match the current filter.</p>
      ) : (
        <div className="space-y-2">
          {groupSurveys(filteredSurveys).map((entry) => {
            if (entry.kind === 'pair') {
              return (
                <PairRow
                  key={entry.pairGroupId}
                  surveys={entry.surveys}
                  clientId={clientId}
                  dbLookup={dbLookup}
                />
              );
            }
            return (
              <SurveyCard key={entry.survey.id} survey={entry.survey} clientId={clientId} dbLookup={dbLookup} />
            );
          })}
        </div>
      )}
    </div>
  );
}
